import { useState, useEffect, useCallback } from 'react';
import { api, Client, Transcript, MetadataResult } from '../lib/api';

const MEETING_TYPES = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'demo', label: 'Demo' },
  { value: 'working_session', label: 'Working Session' },
  { value: 'qbr', label: 'QBR' },
  { value: 'support', label: 'Support' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'other', label: 'Other' },
];

// Non-blocking sanity check for the meeting date: catches year-mismatch typos
// (or a heuristic/LLM extraction guessing the wrong year) without blocking submission.
function getMeetingDateWarning(meetingDate: string): string | null {
  if (!meetingDate) return null;
  const parsed = new Date(`${meetingDate}T00:00:00`);
  if (isNaN(parsed.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.round((today.getTime() - parsed.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return 'This meeting date is in the future — double-check it.';
  }
  if (diffDays > 60) {
    return 'This meeting date is more than 60 days ago — double-check the year.';
  }
  return null;
}

export function Input() {
  const [clients, setClients] = useState<Client[]>([]);
  const [recentTranscripts, setRecentTranscripts] = useState<Transcript[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [transcript, setTranscript] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [clientId, setClientId] = useState('');
  const [meetingType, setMeetingType] = useState('working_session');
  const [attendeesCap8, setAttendeesCap8] = useState('');
  const [attendeesClient, setAttendeesClient] = useState('');

  // Auto-detection state
  const [autoDetected, setAutoDetected] = useState<{
    meeting_date?: boolean;
    client_id?: boolean;
    attendees?: boolean;
  }>({});

  // New client modal
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientStatus, setNewClientStatus] = useState<'prospect_early' | 'prospect_engaged' | 'live_client'>('prospect_early');

  // Merge mode (combine two Granola recordings of the same meeting)
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState<string[]>([]);
  const [mergePreviewOpen, setMergePreviewOpen] = useState(false);
  const [mergeText, setMergeText] = useState('');
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [mergeCommitting, setMergeCommitting] = useState(false);

  const toggleMergeMode = () => {
    setMergeMode(m => !m);
    setMergeSelection([]);
    setMergeError('');
  };

  const toggleMergeSelect = (id: string) => {
    setMergeSelection(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 2 ? [...prev, id] : prev
    );
  };

  // Same-client guard: only same-meeting recordings can be merged
  const selectedTranscripts = recentTranscripts.filter(t => mergeSelection.includes(t.transcript_id));
  const sameClient = selectedTranscripts.length === 2 &&
    selectedTranscripts[0].client_id === selectedTranscripts[1].client_id;
  const canPreviewMerge = mergeSelection.length === 2 && sameClient;

  const handlePreviewMerge = async () => {
    if (!canPreviewMerge) return;
    setMergePreviewOpen(true);
    setMergeText('');
    setMergeError('');
    setMergeLoading(true);
    try {
      await api.mergeTranscriptPreview(mergeSelection[0], mergeSelection[1], {
        onDelta: t => setMergeText(prev => prev + t),
        onError: m => setMergeError(m),
      });
    } catch (err) {
      setMergeError(err instanceof Error ? err.message : 'Failed to generate merge preview');
    } finally {
      setMergeLoading(false);
    }
  };

  const handleConfirmMerge = async () => {
    setMergeCommitting(true);
    setMergeError('');
    try {
      const result = await api.commitTranscriptMerge(mergeSelection[0], mergeSelection[1]);
      setMergePreviewOpen(false);
      setMergeMode(false);
      setMergeSelection([]);
      setSuccess(`${result.message} (new transcript ${result.transcript_id.slice(0, 12)}…)`);
      const transcriptList = await api.listTranscripts({ limit: 10 });
      setRecentTranscripts(transcriptList);
    } catch (err) {
      setMergeError(err instanceof Error ? err.message : 'Failed to commit merge');
    } finally {
      setMergeCommitting(false);
    }
  };

  // Load clients and recent transcripts
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientList, transcriptList] = await Promise.all([
        api.listClients(),
        api.listTranscripts({ limit: 10 }),
      ]);
      setClients(clientList);
      setRecentTranscripts(transcriptList);

      // Set defaults from localStorage
      const lastClient = localStorage.getItem('cap8_last_client');
      const lastMeetingType = localStorage.getItem('cap8_last_meeting_type');
      if (lastClient && clientList.some(c => c.client_id === lastClient)) {
        setClientId(lastClient);
      }
      if (lastMeetingType) {
        setMeetingType(lastMeetingType);
      }
    } catch (err) {
      setError('Failed to load data');
    }
  };

  // Auto-extract metadata when transcript is pasted
  const handleTranscriptChange = useCallback(
    async (text: string) => {
      setTranscript(text);
      setAutoDetected({});

      if (text.length > 100) {
        try {
          const metadata: MetadataResult = await api.extractMetadata(text);

          const detected: typeof autoDetected = {};

          if (metadata.meeting_date && !meetingDate) {
            setMeetingDate(metadata.meeting_date);
            detected.meeting_date = true;
          }

          if (metadata.suggested_client && !clientId) {
            setClientId(metadata.suggested_client.client_id);
            detected.client_id = true;
          }

          if (metadata.attendees.length > 0) {
            const cap8Attendees: string[] = [];
            const clientAttendees: string[] = [];

            metadata.attendees.forEach((a) => {
              const name = a.name || a.email || '';
              if (
                a.organization?.toLowerCase().includes('cap8') ||
                a.email?.toLowerCase().includes('cap8')
              ) {
                cap8Attendees.push(name);
              } else {
                clientAttendees.push(name);
              }
            });

            if (cap8Attendees.length > 0 && !attendeesCap8) {
              setAttendeesCap8(cap8Attendees.join(', '));
              detected.attendees = true;
            }
            if (clientAttendees.length > 0 && !attendeesClient) {
              setAttendeesClient(clientAttendees.join(', '));
              detected.attendees = true;
            }
          }

          setAutoDetected(detected);
        } catch {
          // Silent fail for metadata extraction
        }
      }
    },
    [meetingDate, clientId, attendeesCap8, attendeesClient]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validate
      if (!transcript.trim()) throw new Error('Transcript is required');
      if (!meetingDate) throw new Error('Meeting date is required');
      if (!clientId) throw new Error('Client is required');

      // Submit transcript
      const result = await api.submitTranscript({
        transcript: transcript.trim(),
        meeting_date: meetingDate,
        client_id: clientId,
        attendees_cap8: attendeesCap8.split(',').map((s) => s.trim()).filter(Boolean),
        attendees_client: attendeesClient.split(',').map((s) => s.trim()).filter(Boolean),
        meeting_type: meetingType as any,
        source: 'pasted',
      });

      // Start processing
      await api.processTranscript(result.transcript.transcript_id);

      // Save defaults
      localStorage.setItem('cap8_last_client', clientId);
      localStorage.setItem('cap8_last_meeting_type', meetingType);

      // Clear form and reload
      setTranscript('');
      setMeetingDate('');
      setAttendeesCap8('');
      setAttendeesClient('');
      setAutoDetected({});
      setSuccess('Transcript submitted and processing started');

      // Reload recent transcripts
      const transcriptList = await api.listTranscripts({ limit: 10 });
      setRecentTranscripts(transcriptList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) return;

    try {
      const client = await api.createClient({
        name: newClientName.trim(),
        archetype: 'us_ria',
        aum_bucket: 'unknown',
        region: 'us',
        status: newClientStatus,
        notes: null,
      });
      setClients([...clients, client]);
      setClientId(client.client_id);
      setShowNewClient(false);
      setNewClientName('');
      setNewClientStatus('prospect_early');
    } catch (err) {
      setError('Failed to create client');
    }
  };

  const handleProcess = async (transcriptId: string) => {
    try {
      await api.processTranscript(transcriptId);
      const transcriptList = await api.listTranscripts({ limit: 10 });
      setRecentTranscripts(transcriptList);
    } catch (err) {
      setError('Processing failed');
    }
  };

  const handleDelete = async (transcriptId: string, clientName: string) => {
    if (!confirm(`Delete transcript for ${clientName}? This will also delete all associated insights, commitments, and strategy mentions. This cannot be undone.`)) {
      return;
    }
    try {
      const result = await api.deleteTranscript(transcriptId);
      setSuccess(`Deleted: ${result.deleted.insights} insights, ${result.deleted.commitments} commitments, ${result.deleted.strategy_mentions} strategy mentions`);
      const transcriptList = await api.listTranscripts({ limit: 10 });
      setRecentTranscripts(transcriptList);
    } catch (err) {
      setError('Delete failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const getStatusClass = (status: string) => {
    return `status-${status}`;
  };

  const meetingDateWarning = getMeetingDateWarning(meetingDate);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Submit Transcript</h1>
        <p className="page-subtitle">Paste a Granola transcript or upload a file</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Transcript</label>
          <textarea
            className="form-textarea"
            value={transcript}
            onChange={(e) => handleTranscriptChange(e.target.value)}
            placeholder="Paste your Granola transcript here..."
            style={{ minHeight: '300px' }}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              Meeting Date
              {autoDetected.meeting_date && (
                <span className="auto-detected-badge">Auto-detected</span>
              )}
            </label>
            <input
              type="date"
              className={`form-input ${autoDetected.meeting_date ? 'auto-detected' : ''}`}
              value={meetingDate}
              onChange={(e) => {
                setMeetingDate(e.target.value);
                setAutoDetected((prev) => ({ ...prev, meeting_date: false }));
              }}
            />
            {meetingDateWarning && (
              <p className="form-hint form-hint-warning">{meetingDateWarning}</p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Client
              {autoDetected.client_id && (
                <span className="auto-detected-badge">Auto-detected</span>
              )}
            </label>
            <div className="flex gap-1">
              <select
                className={`form-select ${autoDetected.client_id ? 'auto-detected' : ''}`}
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value);
                  setAutoDetected((prev) => ({ ...prev, client_id: false }));
                }}
              >
                <option value="">Select client...</option>
                {clients
                  .sort((a, b) => {
                    // Sort: clients first, then prospects
                    const aIsProspect = a.status.startsWith('prospect');
                    const bIsProspect = b.status.startsWith('prospect');
                    if (aIsProspect !== bIsProspect) return aIsProspect ? 1 : -1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((c) => (
                    <option key={c.client_id} value={c.client_id}>
                      {c.status.startsWith('prospect') ? `[P] ${c.name}` : c.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={() => setShowNewClient(true)}
              >
                +
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Meeting Type</label>
            <select
              className="form-select"
              value={meetingType}
              onChange={(e) => setMeetingType(e.target.value)}
            >
              {MEETING_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              Cap8 Attendees
              {autoDetected.attendees && (
                <span className="auto-detected-badge">Auto-detected</span>
              )}
            </label>
            <input
              type="text"
              className={`form-input ${autoDetected.attendees ? 'auto-detected' : ''}`}
              value={attendeesCap8}
              onChange={(e) => setAttendeesCap8(e.target.value)}
              placeholder="Santiago, Martha, Joseph"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Client Attendees</label>
            <input
              type="text"
              className="form-input"
              value={attendeesClient}
              onChange={(e) => setAttendeesClient(e.target.value)}
              placeholder="Names or emails, comma-separated"
            />
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Submitting...' : 'Submit & Process'}
        </button>
      </form>

      {/* New Client Modal */}
      {showNewClient && (
        <div className="card mt-2">
          <div className="card-header">
            <span className="card-title">New Client</span>
            <button
              className="btn btn-secondary btn-small"
              onClick={() => setShowNewClient(false)}
            >
              Cancel
            </button>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label">Client Name</label>
              <input
                type="text"
                className="form-input"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={newClientStatus}
                onChange={(e) => setNewClientStatus(e.target.value as any)}
              >
                <option value="prospect_early">Prospect (Early)</option>
                <option value="prospect_engaged">Prospect (Engaged)</option>
                <option value="live_client">Client</option>
              </select>
            </div>
          </div>
          <button className="btn btn-primary btn-small" onClick={handleCreateClient}>
            Create
          </button>
        </div>
      )}

      {/* Recent Transcripts */}
      <div className="mt-2">
        <div className="flex items-center justify-between mb-1">
          <h2 className="card-title" style={{ margin: 0 }}>Recent Transcripts</h2>
          <button
            className={`btn btn-small ${mergeMode ? 'btn-primary' : 'btn-secondary'}`}
            onClick={toggleMergeMode}
            title="Combine two Granola recordings of the same meeting"
          >
            {mergeMode ? 'Cancel merge' : 'Merge recordings'}
          </button>
        </div>

        {mergeMode && (
          <div className="merge-hint">
            <span>
              Select the <strong>two recordings of the same meeting</strong> (e.g. your Granola capture and a colleague's), then preview the reconciled transcript.
            </span>
            <div className="flex gap-1 items-center">
              <span className="text-muted text-small">{mergeSelection.length}/2 selected</span>
              <button
                className="btn btn-primary btn-small"
                disabled={!canPreviewMerge}
                onClick={handlePreviewMerge}
              >
                Preview merge
              </button>
            </div>
          </div>
        )}
        {mergeMode && mergeSelection.length === 2 && !sameClient && (
          <div className="chat-error" style={{ marginBottom: '0.5rem' }}>
            Those two recordings are for different clients. Pick two recordings of the same meeting.
          </div>
        )}

        <div className="status-list">
          {recentTranscripts.length === 0 ? (
            <div className="status-item text-muted">No transcripts yet</div>
          ) : (
            recentTranscripts.map((t) => {
              const selected = mergeSelection.includes(t.transcript_id);
              const selectable = mergeMode && (selected || mergeSelection.length < 2);
              return (
                <div
                  key={t.transcript_id}
                  className={`status-item ${mergeMode ? 'merge-selectable' : ''} ${selected ? 'merge-selected' : ''}`}
                  onClick={mergeMode && selectable ? () => toggleMergeSelect(t.transcript_id) : undefined}
                  style={mergeMode ? { cursor: selectable ? 'pointer' : 'default', opacity: selectable ? 1 : 0.5 } : undefined}
                >
                  <div className="flex items-center gap-1">
                    {mergeMode && (
                      <input
                        type="checkbox"
                        checked={selected}
                        readOnly
                        disabled={!selectable}
                      />
                    )}
                    <div>
                      <span style={{ fontWeight: 500 }}>{t.client_name}</span>
                      <span className="text-muted text-small" style={{ marginLeft: '0.5rem' }}>
                        {t.meeting_date}
                      </span>
                    </div>
                  </div>
                  {!mergeMode && (
                    <div className="flex gap-1 items-center">
                      <span className={getStatusClass(t.processing_status)}>
                        {t.processing_status}
                      </span>
                      {(t.processing_status === 'pending' || t.processing_status === 'error') && (
                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => handleProcess(t.transcript_id)}
                        >
                          Process
                        </button>
                      )}
                      <button
                        className="btn btn-secondary btn-small"
                        style={{ color: 'var(--error)' }}
                        onClick={() => handleDelete(t.transcript_id, t.client_name || 'Unknown')}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Merge preview modal */}
      {mergePreviewOpen && (
        <div className="modal-overlay" onClick={() => !mergeCommitting && setMergePreviewOpen(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Review merged transcript</h2>
              <button className="modal-close" onClick={() => !mergeCommitting && setMergePreviewOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="text-muted text-small" style={{ marginTop: 0 }}>
                The two recordings have been reconciled into one speaker-labeled transcript. Review it below, then confirm to create the merged record and archive the two originals. Archived recordings are kept for recovery but excluded from analytics.
              </p>
              {mergeError && <div className="chat-error">{mergeError}</div>}
              <pre className="merge-preview-text">
                {mergeText || (mergeLoading ? '' : 'No content.')}
                {mergeLoading && <span className="chat-cursor" aria-hidden="true" />}
              </pre>
              {mergeLoading && !mergeText && (
                <div className="chat-stage"><div className="chat-loading"><span></span><span></span><span></span></div><span className="chat-stage-label">Reconciling the two recordings…</span></div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setMergePreviewOpen(false)} disabled={mergeCommitting}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConfirmMerge} disabled={mergeLoading || mergeCommitting || !!mergeError || !mergeText}>
                {mergeCommitting ? 'Merging…' : 'Confirm & merge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
